import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { HttpService } from '../../services/http.service';
import { UserAccountDTO } from '../../classes/user-account.dto';
import { StorageService } from '../../services/storage.service';
import { TranslateModule } from '@ngx-translate/core';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { CommonModule } from '@angular/common';
import { MedicalRecordMenuInfo } from '../../interfaces/medical-history.interface';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { ModalAddNewRecordComponent } from '../modals/modal-add-new-record/modal-add-new-record/modal-add-new-record.component';
import { ModalDoctorDetailsComponent } from '../modals/modal-doctor-details/modal-doctor-details/modal-doctor-details.component';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { DeleteModalComponent } from '../delete-modal/delete-modal.component';
import { Router } from '@angular/router';

@Component({
    selector: 'app-medical-history',
    imports: [TranslateModule, NzDividerModule, CommonModule, NzGridModule, NzIconModule, NzPopoverModule],
    templateUrl: './medical-history.component.html',
    styleUrls: ['./medical-history.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MedicalHistoryComponent implements OnInit, OnDestroy {
  medicalRecordMenuInfo?: MedicalRecordMenuInfo;
  doctorDetails: any = null;
  isDesktop: boolean = true;


  medicalRecordEntries: {
    key: string;
    title: string;
    items: {
      id?: any;
      date: string | null;
      type: string;
      notes: string | '';
      isLast: boolean;
      underTherapy?: boolean;
      specificAllergen?: string;
      drugFormat?: string;
      posology?: string;
      doseMeasureUnit?: string;
      dose?: string;
      posologyFrequence: string;
      revaccinationDate: string | null;
      revaccination: boolean;
    }[];
    count: number;
    isEmpty: boolean;
  }[] = [];
  constructor(
    private http: HttpService,
    private storage: StorageService,
    private modal: NzModalService,
    private router: Router
  ) {}

  ngOnInit() {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData.individualId;

    this.http.getDoctorDetails(individualId).subscribe({
      next: (data) => {
        this.doctorDetails = data;
      },
      error: (error) => {
        console.error(error);
      },
    });
      this.loadMedicalRecords();
      this.checkScreenSize();
      window.addEventListener('resize', this.checkScreenSize.bind(this));  
  }

  loadMedicalRecords() {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData.individualId;
    this.http.getMedicalRecordMenuInfo(individualId).subscribe((response) => {
      this.medicalRecordMenuInfo = response;
      this.transformMedicalRecords();
    });
  }

  private transformMedicalRecords() {
    if (!this.medicalRecordMenuInfo) return;

    const allowedKeys = ['pathology', 'allergen', 'surgical', 'vaccination', 'drug'];

    this.medicalRecordEntries = allowedKeys.map((key) => {
      const record = (this.medicalRecordMenuInfo as Record<string, any>)[key];
      const count = this.medicalRecordMenuInfo![`${key}Count`] || 0;
      const items = record
        ? [
            {
              id: record.id,
              date: record.onSetDate || record.eventDate || record.reminderStartDate || record.vaccinationDate || null,
              type: record.name || record.drugType || record.intervention || '',
              notes: record.notes || '',
              isLast: true,
              underTherapy: record?.underTherapy,
              specificAllergen: record?.name ? record?.specificAllergen : null,
              drugFormat: record?.drugType ? record?.drugFormat : null,
              posology: record?.dose,
              doseMeasureUnit: record?.doseMeasureUnit,
              dose: record?.posology,
              posologyFrequence: record?.posologyFrequence,
              revaccinationDate: record?.revaccinationDate,
              revaccination: record?.revaccination,
            },
          ]
        : [];

      return {
        key,
        title: key.charAt(0).toUpperCase() + key.slice(1),
        items,
        count,
        isEmpty: count === 0,
      };
    });
  }

  openModal(key: string) {
    const modalRef = this.modal.create({
      nzContent: ModalAddNewRecordComponent,
      nzData: { key },
      nzFooter: null,
    });

    modalRef.afterClose.subscribe((result) => {
      if (result) {
        this.loadMedicalRecords();
      }
    });
  }

  editRecord(record: any, item: any): void {
    const modalRef = this.modal.create({
      nzContent: ModalAddNewRecordComponent,
      nzData: {
        key: record.key,
        initialData: {
          ...item,
          underTherapy: item.underTherapy || false,
          specificAllergen: item.type ? item.specificAllergen : null,
          drugFormat: item.type ? item.drugFormat : null,
          posology: item.dose || null,
          doseMeasureUnit: item.doseMeasureUnit || null,
          dose: item.posology || null,
          posologyFrequence: item.posologyFrequence || null,
          revaccinationDate: item.revaccinationDate || null,
          revaccination: item.revaccination || false,
        },
      },
      nzFooter: null,
    });

    modalRef.afterClose.subscribe((updatedRecord) => {
      if (updatedRecord) {
        const recordIndex = this.medicalRecordEntries.findIndex((r) => r.key === record.key);
        if (recordIndex !== -1) {
          const itemIndex = this.medicalRecordEntries[recordIndex].items.findIndex((i) => i.id === item.id);
          if (itemIndex !== -1) {
            this.medicalRecordEntries[recordIndex].items[itemIndex] = {
              ...this.medicalRecordEntries[recordIndex].items[itemIndex],
              ...updatedRecord,
            };
          }
        }
        this.loadMedicalRecords();
      }
    });
  }

  deleteRecord(record: any, item: any): void {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData.individualId;
    const payload = this.buildDeletePayload(record.key, item);

    if (!payload) {
      console.error('Errore: Payload non valido per', record.key);
      return;
    }

    this.http.deleteMedicalRecord(record.key, individualId, payload).subscribe({
      next: () => {
        this.loadMedicalRecords();
        const recordIndex = this.medicalRecordEntries.findIndex((r) => r.key === record.key);
        if (recordIndex !== -1) {
          this.medicalRecordEntries[recordIndex].items = this.medicalRecordEntries[recordIndex].items.filter((i) => i !== item);
        }
      },
      error: (error) => {
        console.error("Errore durante l'eliminazione:", error);
      },
    });
  }

  delete(record: any, item: any): void {
    const modalRef = this.modal.create({
      nzTitle: 'Elimina valore',
      nzContent: DeleteModalComponent,
      nzData: {
        message: `Confermi di voler eliminare il valore di ${item.type || 'questo record'}?`,
      },
      nzFooter: null,
    });

    modalRef.afterClose.subscribe((confirmed) => {
      if (confirmed) {
        this.deleteRecord(record, item);
      }
    });
  }

  openDetails() {
    const modalRef = this.modal.create({
      nzContent: ModalDoctorDetailsComponent,
      nzData: { doctorDetails: this.doctorDetails },
      nzFooter: null,
    });

    modalRef.afterClose.subscribe((updatedDoctor) => {
      if (updatedDoctor) {
        this.doctorDetails = updatedDoctor;
      }
    });
  }

  private buildDeletePayload(key: string, item: any): any {
    const recordDetails = this.medicalRecordMenuInfo?.[key];

    if (!recordDetails) {
      console.error(`Errore: Nessun dettaglio trovato per ${key}`);
      return null;
    }

    switch (key) {
      case 'pathology':
        return {
          name: recordDetails.name,
          onSetDate: recordDetails.onSetDate,
          diagnosysDate: recordDetails.diagnosysDate,
          underTherapy: recordDetails.underTherapy || false,
          notes: item.notes || recordDetails.notes,
          ecCodeId: recordDetails.ecCodeId,
          id: recordDetails.id,
          codeId: recordDetails.codeId,
          creationDatetime: recordDetails.creationDatetime,
          lastUpdateDatetime: recordDetails.lastUpdateDatetime,
        };

      case 'surgical':
        return {
          bodyZone: recordDetails.bodyZone,
          intervention: recordDetails.intervention,
          eventDate: recordDetails.eventDate,
          notes: item.notes || recordDetails.notes,
          ecCodeId: recordDetails.ecCodeId,
          id: recordDetails.id,
          codeId: recordDetails.codeId,
          creationDatetime: recordDetails.creationDatetime,
          lastUpdateDatetime: recordDetails.lastUpdateDatetime,
        };

      case 'allergen':
        return {
          name: recordDetails.name,
          specificAllergen: recordDetails.specificAllergen,
          onSetDate: recordDetails.onSetDate,
          diagnosysDate: recordDetails.diagnosysDate,
          notes: item.notes || recordDetails.notes,
          ecCodeId: recordDetails.ecCodeId,
          specificECCodeId: recordDetails.specificECCodeId,
          id: recordDetails.id,
          codeId: recordDetails.codeId,
          creationDatetime: recordDetails.creationDatetime,
          lastUpdateDatetime: recordDetails.lastUpdateDatetime,
        };

      case 'drug':
        return {
          drugType: recordDetails.drugType,
          activeSubstance: recordDetails.activeSubstance,
          commercialName: recordDetails.commercialName,
          dose: recordDetails.posology,
          doseMeasureUnit: recordDetails.doseMeasureUnit,
          posology: recordDetails.dose,
          posologyFrequence: recordDetails.posologyFrequence,
          remindMePill: false,
          reminderStartDate: recordDetails.reminderStartDate,
          reminderEndDate: recordDetails.reminderEndDate,
          activePrincipleKey: recordDetails.activePrincipleKey,
          drugKey: recordDetails.drugKey,
          reminderStartTimeHH: 0,
          reminderStartTimeMM: 0,
          reminderEndTimeHH: 0,
          reminderEndTimeMM: 0,
          drugFormat: recordDetails.drugFormat,
          formatId: recordDetails.formatId,
          id: recordDetails.id,
          codeId: recordDetails.codeId,
          creationDatetime: recordDetails.creationDatetime,
          lastUpdateDatetime: recordDetails.lastUpdateDatetime,
        };

      case 'vaccination':
        return {
          name: recordDetails.name,
          vaccinationDate: recordDetails.vaccinationDate,
          revaccination: recordDetails.revaccination || false,
          revaccinationDate: recordDetails.revaccinationDate,
          id: recordDetails.id,
          codeId: recordDetails.codeId,
          creationDatetime: recordDetails.creationDatetime,
          lastUpdateDatetime: recordDetails.lastUpdateDatetime,
        };

      default:
        console.error(`Tipo ${key} non gestito`);
        return null;
    }
  }

  checkScreenSize() {
    this.isDesktop = window.innerWidth >= 768;
  }
  
  ngOnDestroy() {
    window.removeEventListener('resize', this.checkScreenSize.bind(this));
  }
  
  
  protected showAll(type: string) {
    this.router.navigate(['/health-folder/all-history'], { queryParams: { type } });
  }
}
